import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Calendar, Mail, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
    const { slug } = useParams();
    const { getLocalizedPath } = useLanguage();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await axios.get(`${API}/blog/${slug}`);
                setPost(response.data);
            } catch (err) {
                setError('Post not found');
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-background">
                <nav className="fixed top-0 left-0 right-0 z-50 glass">
                    <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                        <Link to={getLocalizedPath('/')} className="flex items-center gap-2">
                            <Mail className="w-8 h-8 text-primary" />
                            <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
                        </Link>
                    </div>
                </nav>

                <div className="pt-28 px-6">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="font-heading text-3xl font-bold text-foreground mb-4">Post Not Found</h1>
                        <p className="text-muted-foreground mb-6">The blog post you're looking for doesn't exist.</p>
                        <Link to={getLocalizedPath('/')}>
                            <Button className="rounded-full bg-primary text-primary-foreground">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <SEO
                title={post.title}
                description={post.content.substring(0, 160).replace(/<[^>]*>/g, '')}
                path={`/blog/${post.slug}`}
            />
            <div className="min-h-screen bg-background">
                {/* Navigation */}
                <nav className="fixed top-0 left-0 right-0 z-50 glass">
                    <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                        <Link to={getLocalizedPath('/')} className="flex items-center gap-2">
                            <Mail className="w-8 h-8 text-primary" />
                            <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
                        </Link>
                        <Link to={getLocalizedPath('/')}>
                            <Button variant="ghost" className="text-foreground hover:text-primary transition-colors">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </Link>
                    </div>
                </nav>

                {/* Content */}
                <main className="pt-28 pb-16 px-6">
                    <article className="max-w-4xl mx-auto">
                        <Card className="bg-card border-border overflow-hidden">
                            <CardContent className="p-8 md:p-12">
                                {/* Header */}
                                <div className="mb-8">
                                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                                        {post.title}
                                    </h1>
                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            {new Date(post.created_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* Divider */}
                                <div className="w-full h-px bg-border mb-8" />

                                {/* Content */}
                                <div
                                    className="prose prose-invert prose-lg max-w-none
                    prose-headings:font-heading prose-headings:text-foreground
                    prose-p:text-foreground/90 prose-p:leading-relaxed
                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-foreground
                    prose-code:text-primary prose-code:bg-secondary prose-code:px-1 prose-code:rounded
                    prose-pre:bg-secondary prose-pre:border prose-pre:border-border
                    prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground
                    prose-li:text-foreground/90
                    prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
                    prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4
                    [&_li]:my-1 [&_li]:pl-1"
                                    dangerouslySetInnerHTML={{ __html: post.content }}
                                />
                            </CardContent>
                        </Card>

                        {/* Back link */}
                        <div className="mt-8 text-center">
                            <Link to={getLocalizedPath('/')}>
                                <Button variant="outline" className="rounded-full border-border hover:border-primary">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back to Instant-Email
                                </Button>
                            </Link>
                        </div>
                    </article>
                </main>
            </div>
        </>
    );
};

export default BlogPost;
